import { PHASE, PLAYER } from "../../../../common/constants.ts";
import type { Phase } from "../../../../common/types.ts";

export type MobileSection = "home" | "team" | "games" | "market" | "league";
export type MobilePageDomain = MobileSection | "system" | "outsideLeague";
export type MobilePageMode = "standard" | "fullscreen" | "outsideLeague";
export type MobileNavigationIcon = "games" | "home" | "league" | "market" | "team";

export type MobileNavigationContext = {
  hasOpenFreeAgency?: boolean;
  hasPendingNegotiations?: boolean;
  lastMarketPageID?: MarketPageID;
  needsExpansionProtection?: boolean;
  originSection?: MobileSection;
  phase: Phase;
  preserveOriginSection?: boolean;
  playerTid?: number | null;
};

export type MobileNavigationItem = {
  key: MobileSection;
  defaultLabel: string;
  icon: MobileNavigationIcon;
  labelKey: string;
  resolveRoot: (context: MobileNavigationContext) => string[];
};

const pageOwnership = {
  home: ["leagueDashboard"],
  team: ["roster", "player", "teamFinances"],
  games: ["dailySchedule", "gameLog", "liveGame", "playoffs"],
  market: [
    "marketHub",
    "draft",
    "draftHistory",
    "draftLottery",
    "draftScouting",
    "freeAgents",
    "negotiationList",
    "protectPlayers",
    "trade",
  ],
  league: ["history", "leaders", "news", "playerRatings", "playerStats", "standings", "teamStats"],
  system: [],
  outsideLeague: ["dashboard", "newLeague"],
} as const satisfies Record<MobilePageDomain, readonly string[]>;

export const mobilePageOwnership = pageOwnership;

const marketRootByPageID = {
  marketHub: ["market"],
  draft: ["draft"],
  draftHistory: ["draft_history"],
  draftLottery: ["draft_lottery"],
  draftScouting: ["draft_scouting"],
  freeAgents: ["free_agents"],
  negotiationList: ["negotiation"],
  protectPlayers: ["protect_players"],
  trade: ["trade"],
} as const satisfies Record<(typeof pageOwnership.market)[number], readonly string[]>;

export type MarketPageID = keyof typeof marketRootByPageID;

const sectionForPageID = new Map<string, MobilePageDomain>(
  Object.entries(pageOwnership).flatMap(([section, pageIDs]) =>
    pageIDs.map((pageID) => [pageID, section as MobilePageDomain]),
  ),
);

const shouldFailOnUnknownPageID = () =>
  typeof process !== "undefined" && process.env.NODE_ENV === "development";

const isDraftPhase = (phase: Phase) =>
  phase === PHASE.EXPANSION_DRAFT ||
  phase === PHASE.FANTASY_DRAFT ||
  phase === PHASE.DRAFT ||
  phase === PHASE.AFTER_DRAFT;

const copyRoute = (route: readonly string[]) => [...route];

const resolvePlayerSection = (
  context?: Pick<MobileNavigationContext, "originSection" | "playerTid">,
): MobileSection => {
  if (context?.originSection) {
    return context.originSection;
  }

  switch (context?.playerTid) {
    case PLAYER.FREE_AGENT:
    case PLAYER.UNDRAFTED:
    case PLAYER.UNDRAFTED_FANTASY_TEMP:
    case PLAYER.UNDRAFTED_2:
    case PLAYER.UNDRAFTED_3:
      return "market";
    case PLAYER.RETIRED:
    case PLAYER.DOES_NOT_EXIST:
    case PLAYER.TOT:
      return "league";
    default:
      return "team";
  }
};

export const resolveMarketRoot = ({
  hasOpenFreeAgency,
  hasPendingNegotiations,
  lastMarketPageID,
  needsExpansionProtection,
  phase,
}: MobileNavigationContext): string[] => {
  if (hasPendingNegotiations || phase === PHASE.RESIGN_PLAYERS) {
    return ["negotiation"];
  }

  if (hasOpenFreeAgency || phase === PHASE.FREE_AGENCY) {
    return ["free_agents"];
  }

  if (phase === PHASE.DRAFT_LOTTERY) {
    return ["draft_lottery"];
  }

  if (needsExpansionProtection || phase === PHASE.EXPANSION_DRAFT) {
    return ["protect_players"];
  }

  if (isDraftPhase(phase)) {
    return ["draft"];
  }

  // M3-4: the draft-picks page was retired into the DraftPicksSheet overlay
  // (opened in place from the market hub), so the market tab lands on the hub —
  // which hosts the "My Picks" sheet opener — rather than a dead /draft_picks
  // route during the trade-frozen phases.
  if (phase === PHASE.AFTER_TRADE_DEADLINE || phase === PHASE.PLAYOFFS) {
    return ["market"];
  }

  if (lastMarketPageID !== undefined) {
    return copyRoute(marketRootByPageID[lastMarketPageID]);
  }

  return ["trade"];
};

export const resolveMobileSection = (
  pageID: unknown,
  context?: Partial<
    Pick<MobileNavigationContext, "originSection" | "playerTid" | "preserveOriginSection">
  >,
): MobilePageDomain | undefined => {
  if (typeof pageID !== "string") {
    return undefined;
  }

  if (pageID === "player") {
    return resolvePlayerSection(context);
  }

  if (context?.preserveOriginSection && context.originSection) {
    return context.originSection;
  }

  const section = sectionForPageID.get(pageID);
  if (section === undefined && shouldFailOnUnknownPageID()) {
    throw new Error(`Missing mobile section owner for pageID "${pageID}"`);
  }

  return section;
};

export const resolveMobilePageMode = (pageID: unknown): MobilePageMode => {
  if (pageID === "liveGame") {
    return "fullscreen";
  }

  if (pageID === "dashboard" || pageID === "newLeague") {
    return "outsideLeague";
  }

  return "standard";
};

export const mobileNavigationItems: MobileNavigationItem[] = [
  {
    key: "home",
    defaultLabel: "Home",
    icon: "home",
    labelKey: "tabBar.home",
    resolveRoot: () => [],
  },
  {
    key: "team",
    defaultLabel: "Team",
    icon: "team",
    labelKey: "tabBar.team",
    resolveRoot: () => ["roster"],
  },
  {
    key: "games",
    defaultLabel: "Games",
    icon: "games",
    labelKey: "tabBar.games",
    resolveRoot: () => ["daily_schedule"],
  },
  {
    key: "market",
    defaultLabel: "Market",
    icon: "market",
    labelKey: "tabBar.market",
    resolveRoot: () => ["market"],
  },
  {
    key: "league",
    defaultLabel: "League",
    icon: "league",
    labelKey: "tabBar.league",
    resolveRoot: () => ["standings"],
  },
];

export const resolveMobileNavigationRoot = (key: MobileSection, context: MobileNavigationContext) =>
  mobileNavigationItems.find((item) => item.key === key)?.resolveRoot(context) ?? [];
